/**
 * Module: screens/creator/steps/CategoriesStep
 * Role: Wizard Step 4/8 — content categories chip-select (FTR-1131 chrome
 *       rewrite). Renders Russian labels (see _labels.ts) while persisting
 *       the underlying enum values into the wizard store.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/useCreatorOptions:useCreatorOptions (BUG-1144 auth-gated fetch)
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - screens/creator/_labels:categoryLabel
 *   - screens/creator/steps/_ProfileChips:Chips
 *   - screens/flow/atoms/HintBar
 *
 * Used by: CreatorOnboardingScreen.tsx
 * ADR-023: no window.Telegram / platform literals outside src/platform/
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreatorOptions } from '../useCreatorOptions';
import { useWizardStore } from '../store';
import { categoryLabel } from '../_labels';
import { HintBar } from '../../flow/atoms/HintBar';
import { P, T } from '../../../styles/tokens';
import { Chips } from './_ProfileChips';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

export default function CategoriesStep() {
  const navigate = useNavigate();
  const { draft, setProfile } = useWizardStore();

  const [categories, setCategories] = useState<string[]>(draft.categories);
  const [error, setError] = useState<string | null>(null);

  const { options, isLoading: optLoading } = useCreatorOptions();

  const items = useMemo(
    () => (options?.categories ?? []).map((v) => ({ value: v, label: categoryLabel(v) })),
    [options?.categories],
  );

  const handleBack = () => navigate(-1);

  function handleNext() {
    if (categories.length === 0) {
      setError('Выберите хотя бы одну категорию'); return;
    }
    setError(null);
    setProfile({ categories });
    navigate('/creator-onboarding/formats');
  }

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={4} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Категории
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Темы вашего контента — выберите хотя бы одну
          </div>
        </div>

        {optLoading && (
          <div style={{ color: P.inkSoft, font: T.subhead }}>Загрузка…</div>
        )}

        {!optLoading && (
          <Chips
            items={items}
            selected={categories}
            onToggle={(c) =>
              setCategories((p) =>
                p.includes(c) ? p.filter((x) => x !== c) : [...p, c],
              )
            }
          />
        )}

        {error && <HintBar>{error}</HintBar>}
      </WizardBody>
      <WizardCtaBar label="Далее" onClick={handleNext} />
    </WizardFrame>
  );
}
